import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";

import { MUIIcons, P } from "../../../../AbstractElements";
import { ALL_USERS_STATUS } from "../../../../Api/constants";
import FilterSliderModal from "../../../../CommonElements/FilterSliderModal";
import CommonModal from "../../../../CommonElements/Modal";
import SearchBar from "../../../../CommonElements/SearchBar";
import NoData from "../../../../container/NoData";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  deleteScheduled,
  getStandaloneScheduleList,
  setScheduledDetailsToNull,
  setStandaloneScheduleToInitial,
} from "../../../../ReduxToolkit/Reducers/AssessmentCatlogSlice";
import { getUsersList } from "../../../../ReduxToolkit/Reducers/UserSlice";
import {
  Action,
  AreYouSureDeleteSchedule,
  AssessmentName,
  Confirm,
  CreatedBy,
  CreatedOn,
  DeleteSchedule,
  EndDate,
  NoDataText,
  NoOfUser,
  SchedulePeriod,
  Select,
  StartDate,
  Status,
} from "../../../../utils/Constant";
import {
  createTooltipCell,
  formatCustomDate,
  standaloneScheduleStatusDropValues,
} from "../../../../utils/helper/helper";
import "./style.scss";

const StandaloneSchedules = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState("");
  const [noDataFound, setNoDataFound] = useState(false);
  const [clearData, setClearData] = useState(false);
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const { usersList } = useAppSelector(state => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userMap, setUserMap] = useState(null);
  const { standaloneScheduleList } = useAppSelector(
    state => state.assessmentCatlog
  );
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");

  useEffect(() => {
    dispatch(getUsersList({ role: ALL_USERS_STATUS }));
    getAssessments();
  }, [dispatch]);

  const getAssessments = async (params = {}) => {
    dispatch(setStandaloneScheduleToInitial());
    dispatch(getStandaloneScheduleList(params)).then((response: any) => {
      if (response?.payload?.assessments?.length === 0) {
        setNoDataFound(true);
      } else {
        setNoDataFound(false);
      }
    });
  };

  useEffect(() => {
    const userMapObj = {};
    usersList?.users?.forEach(user => {
      userMapObj[user.id] = user;
    });
    setUserMap(userMapObj);
  }, [usersList]);

  const handleSearch = (query: string) => {
    setClearData(false);
    setSearchResults(query);
  };

  const onEditClick = (id, assessmentId, type) => {
    dispatch(setScheduledDetailsToNull());
    return navigate(`${process.env.PUBLIC_URL}/assessments/schedule-details`, {
      state: { scheduleId: id, assessmentId: assessmentId, type: type },
    });
  };

  const onDeleteSchedule = (id, assessmentId) => {
    setSelectedUserId(id);
    setSelectedAssessmentId(assessmentId);
    toggleModal();
  };

  const toggleModal = () => {
    if (isModalOpen) {
      setSelectedUserId("");
      setSelectedAssessmentId("");
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleDone = async (values: { [key: string]: string[] }) => {
    setClearData(true);
    const fromDate = values["from_date"] ? values["from_date"][0] : "";
    const toDate = values["to_date"] ? values["to_date"][0] : "";
    const status = values["status"] ? values["status"].join(",") : "";

    const formattedFromDate = fromDate
      ? new Date(fromDate).toLocaleDateString("en-CA").split("T")[0]
      : "";
    const formattedToDate = toDate
      ? new Date(toDate).toLocaleDateString("en-CA").split("T")[0]
      : "";

    getAssessments({
      from_date: formattedFromDate,
      to_date: formattedToDate,
      status: status,
    });
  };

  const handleClear = async () => {
    setClearData(true);
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    getAssessments();
  };

  const renderRequestTable = () => {
    const tableColumns = [
      {
        name: AssessmentName,
        selector: row => row.assessment_name,
        cell: (row: any) =>
          createTooltipCell(
            row.assessment_name,
            `schedule-name-${row.schedule_id}`
          ),
        sortable: true,
        minWidth: "18rem",
      },
      {
        name: CreatedBy,
        selector: row => (userMap ? userMap[row?.created_by]?.user_name : null),
        minWidth: "12rem",
        sortable: true,
      },
      {
        name: CreatedOn,
        selector: row => formatCustomDate(row?.created_at),
        minWidth: "10rem",
        sortable: true,
        sortFunction: (rowOne, rowTwo) => {
          const parseDate = (dateStr: string | null | undefined) => {
            if (!dateStr) return 0;
            return new Date(dateStr).getTime();
          };
          const firstParsedDate = parseDate(rowOne.created_at);
          const secondParsedDate = parseDate(rowTwo.created_at);
          return firstParsedDate - secondParsedDate;
        },
      },
      {
        name: SchedulePeriod,
        selector: row =>
          formatCustomDate(row?.start_date) +
          " - " +
          formatCustomDate(row?.end_date),
        minWidth: "15rem",
        sortable: true,
        sortFunction: (rowOne, rowTwo) => {
          const parseDate = (dateStr: string | null | undefined) => {
            if (!dateStr) return 0;
            return new Date(dateStr).getTime();
          };
          const firstParsedDate = parseDate(rowOne.start_date);
          const secondParsedDate = parseDate(rowTwo.start_date);
          return firstParsedDate - secondParsedDate;
        },
      },
      {
        name: NoOfUser,
        selector: row => row.participant_count,
        minWidth: "10rem",
        sortable: true,
      },
      {
        name: Status,
        selector: row => (
          <div className={row.status}>
            {row?.status?.toString().charAt(0).toLocaleUpperCase() +
              row?.status?.toString().slice(1)}
          </div>
        ),
        minWidth: "10rem",
        sortable: true,
      },
      {
        name: Action,
        selector: row => row.action,
        minWidth: "6rem",
        center: true,
        cell: row => (
          <div>
            <MUIIcons
              size={24}
              className="primary-icon-color pointer"
              iconName={
                row?.status === "upcoming"
                  ? "EditOutlined"
                  : "VisibilityOutlined"
              }
              onClick={() =>
                onEditClick(
                  row?.schedule_id,
                  row?.assessment_id,
                  row?.status === "active" ? "current" : row?.status
                )
              }
            />
            {row?.status === "upcoming" && (
              <MUIIcons
                iconName="DeleteOutlineOutlined"
                className="primary-icon-color pointer ms-3"
                size={24}
                onClick={() =>
                  onDeleteSchedule(row?.schedule_id, row?.assessment_id)
                }
              />
            )}
          </div>
        ),
      },
    ];

    const filteredAssessment = standaloneScheduleList?.schedules?.filter(
      assessment =>
        assessment?.assessment_name
          ?.toLowerCase()
          ?.includes(searchResults?.toLowerCase())
    );

    return (
      <Col>
        <Card className="p-1">
          <CardBody>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-2">
              <div></div>
              <div className="d-flex justify-content-end align-items-center mt-sm-0 mt-2 gap-2 ms-auto">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                />
                <div>
                  <FilterSliderModal
                    dropdowns={[
                      {
                        label: StartDate,
                        key: "from_date",
                        tooltipText: StartDate,
                        isDate: true,
                        isMultiSelect: false,
                      },
                      {
                        label: EndDate,
                        key: "to_date",
                        tooltipText: EndDate,
                        isDate: true,
                        isMultiSelect: false,
                      },
                      {
                        label: Status,
                        key: "status",
                        tooltipText: `${Select} ${Status}`,
                        options: standaloneScheduleStatusDropValues,
                        isMultiSelect: true,
                      },
                    ]}
                    selectedFilterValues={selectedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                    setDefaultFilterValues={setDefaultFilterValues}
                    onDone={handleDone}
                    onClear={handleClear}
                  />
                </div>
              </div>
            </div>
            <DataTable
              className="standalone-schedule-list__table"
              columns={tableColumns}
              data={filteredAssessment}
              pagination
              fixedHeader
              striped
            />
          </CardBody>
        </Card>
      </Col>
    );
  };

  const renderAssessmentContent = () => {
    if (noDataFound) {
      return (
        <NoData svg={"empty-folder-icon"} title={NoDataText} showCard={false} />
      );
    }

    return renderRequestTable();
  };

  return (
    <div>
      <CommonModal
        backdrop="static"
        size="lg"
        isOpen={isModalOpen}
        toggle={toggleModal}
        sizeTitle={DeleteSchedule}
        showFooter={true}
        primaryBtnText={Confirm}
        onPrimaryBtnClick={async () => {
          const payload = {
            id: selectedUserId,
            assessment_id: selectedAssessmentId,
          };
          await dispatch(deleteScheduled({ data: payload }));
          getAssessments();
          toggleModal();
        }}
      >
        <P>{AreYouSureDeleteSchedule}</P>
      </CommonModal>
      <Row className="mt-2">{renderAssessmentContent()}</Row>
    </div>
  );
};
export default StandaloneSchedules;
